// Adapter — maps the model's `SqlClause` to IntelliSense's
// `CompletionPositionKind`. The model layer (`sql-grammar/document/
// editorContextClause.ts`'s clause + paren-frame state machine) is the
// source of truth for clause/paren classification after GRA-7 Slice A6.
// This adapter is the bridge that lets IntelliSense's `deriveCompletionContext`
// (and any future resolver-side consumer of `getEditorContext`) source
// position kind from the model without leaking model types into the
// resolvers' hot paths.
//
// Total function — every `SqlClause` arm has an explicit case so adding a
// new arm in the future fails the exhaustiveness check at the bottom rather
// than silently falling through. `null` clause (no enclosing clause yet —
// e.g. the cursor sits before any keyword in an empty document) and
// `'other'` both map to `'unknown'`.

import type { CompletionPositionKind } from '@/core/engines';
import type { SqlClause } from '@/features/sql-grammar/model';

/** Map a model-layer `SqlClause` (plus the cursor's dotted-base presence)
 *  to IntelliSense's `CompletionPositionKind`. */
export function sqlClauseToCpk(
  clause: SqlClause | null,
  hasDottedBase: boolean,
): CompletionPositionKind {
  // Dotted base always wins — `schema.table.|` is `'dotted-access'`
  // regardless of which clause the dotted reference sits in.
  if (hasDottedBase) return 'dotted-access';
  if (clause === null) return 'unknown';

  switch (clause) {
    // ---- 1:1 (different names) ----
    case 'select': return 'select-list';
    case 'insert-values': return 'values';
    case 'update-set': return 'set';

    // ---- 1:1 (exact names) ----
    case 'from': return 'from';
    case 'where': return 'where';
    case 'group-by': return 'group-by';
    case 'having': return 'having';
    case 'order-by': return 'order-by';
    case 'join-on': return 'join-on';
    case 'limit-offset': return 'limit-offset';
    case 'returning': return 'returning';
    case 'cte-def': return 'cte-def';
    case 'subquery': return 'subquery';

    // ---- collapse ----
    // DELETE's WHERE is the same completion surface as SELECT's WHERE.
    case 'delete-where': return 'where';

    // ---- INSERT column-list parens collapse to `'values'` ----
    // The column list inside `INSERT INTO t (|col_list)` is a values-adjacent
    // slot — both name the columns that will receive values. Resolvers'
    // `'values'` branches already emit the target table's columns through the
    // INSERT-target machinery, so collapsing here is preferable to a dedicated
    // CPI arm. Keeps the position-kind taxonomy small and lets the F5.3
    // INSERT-target emit close script.9 §1/§2 without a new arm. ----
    case 'insert-columns': return 'values';

    // ---- fallback ----
    case 'other': return 'unknown';

    default: {
      // Exhaustiveness guard — if SqlClause gains a new arm this fails the
      // type-check rather than silently falling to `'unknown'`.
      const _exhaustive: never = clause;
      void _exhaustive;
      return 'unknown';
    }
  }
}

// ---------------------------------------------------------------------------
// stripCommentsPreserving — shared comment-stripping primitive
// ---------------------------------------------------------------------------
//
// Retained after plan19 T7 retired the string-walk DDL slot classifier
// (`classifyDdlPositionKind`, F5.2-CB7) that used to live below it. The nine
// DDL slot kinds are now produced token-based by
// `sql-grammar/document/editorContextSlot.ddl2.classifyDdlSlot`, routed through
// the slot seam (`resolveSlotKind` → `deriveCompletionContext`'s DDL-override
// gate) — see `ddlPositionEquivalence.test.ts` for the corpus that gated the
// migration. This helper survives because `isAtExecTargetSlot.ts`,
// `isAtInsertTargetSlot.ts`, and `isAtNextValueForSlot.ts` still scan
// comment-stripped statement tails.

/** Strip line + block comments from a slice of source so the surface-keyword
 *  scan isn't fooled by `/* REFERENCES *\/` or trailing `-- …` comments.
 *  Whitespace-preserves to keep offsets stable for the regex tests.
 *
 *  We don't strip strings here — the callers run after the resolver's
 *  `insideStringOrComment` short-circuit in `deriveCompletionContext`.
 */
export function stripCommentsPreserving(s: string): string {
  let out = '';
  let i = 0;
  while (i < s.length) {
    const c = s[i]!;
    const n = s[i + 1];
    if (c === '-' && n === '-') {
      // Line comment to end-of-line.
      while (i < s.length && s[i] !== '\n') {
        out += ' ';
        i += 1;
      }
      continue;
    }
    if (c === '/' && n === '*') {
      // Block comment to closing `*/`.
      out += '  ';
      i += 2;
      while (i < s.length && !(s[i] === '*' && s[i + 1] === '/')) {
        out += s[i] === '\n' ? '\n' : ' ';
        i += 1;
      }
      if (i < s.length) {
        out += '  ';
        i += 2;
      }
      continue;
    }
    out += c;
    i += 1;
  }
  return out;
}
